import * as React from "react";
import { Link } from "react-router-dom";
import {
  applySupplierCosts,
  createPackage,
  deletePackage,
  listPackages,
  listSettings,
  putSetting,
  setPackageDiscounts,
  updatePackage,
  type AdminPackageRow,
} from "@/api/admin";
import type { PackageWrite, WriteOk } from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { savedNotice } from "@/lib/saved";
import { megabytes, money } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmModal } from "@/components/ui/modal";
import { Notice, Warnings } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { DataTable } from "@/components/ui/paged-list";
import { TD, TH, TR } from "@/components/ui/table";
import { PackageModal } from "@/screens/products/PackageForm";
import { BulkDiscount } from "@/screens/products/BulkDiscount";
import { SupplierCosts } from "@/screens/products/SupplierCosts";
import { BelowCostConfirm, useBelowCostQuestion } from "@/components/BelowCostConfirm";

/**
 * The price list, as something an operator can change.
 *
 * "PRODUCTS" IS THE OPERATOR'S WORD FOR THE `packages` TABLE. It appears in the
 * route, the menu, this title and this filename, and nowhere else -- the API,
 * the types, the worker and the database all say "package".
 *
 * It was read-only until this screen existed: the prices this shop charges were
 * a migration, so changing one meant a developer and a deploy.
 *
 * THREE THINGS THIS SCREEN SAYS OUT LOUD, because each is a way an operator
 * would otherwise be wrong while everything looked right.
 *
 * 1. A SAVED PRICE IS CHARGED AT ONCE, AND SHOWN LATER. The checkout reads
 *    the price from the database when an order opens, so a save changes what
 *    the next customer pays immediately. The page around it -- plan cards,
 *    comparison table -- is baked at build time and keeps the old number until
 *    Publish. An earlier version of this notice said the opposite ("does not
 *    reach the shop on its own"), which is the one reading that would let an
 *    operator try a price out on the live checkout.
 *
 * 2. `esimgo_name` IS THE SUPPLIER'S BUNDLE. A name the live eSIM Go catalogue
 *    does not carry fails with "Bundle not available" AFTER the customer has
 *    paid -- so the field carries that warning rather than being one more box.
 *
 * 3. A PACKAGE SOMEBODY BOUGHT CANNOT BE DELETED. `orders.package_id`
 *    references it and D1 does not enable `PRAGMA foreign_keys`, so the delete
 *    would SUCCEED and orphan every order that ever bought it. The worker
 *    counts first and refuses; this screen disables the button and says why IN
 *    THE ROW -- a `title` on a disabled button never shows, because the button
 *    takes no pointer events.
 *
 * Open orders are NOT repriced by an edit here, and that needs no guard:
 * `orders` snapshots `unit_retail_cents` when it opens and a table CHECK pins
 * `total_cents` to the snapshot.
 */
export function ProductsScreen() {
  const { data, loading, error, reload } = useAsync(() => listPackages(), []);
  // The plan the storefront's buy panel starts on, set from this list.
  const settings = useAsync(() => listSettings(), []);
  const defaultId = settings.data?.settings.find((s) => s.key === "default_package_id")?.value;
  const action = useAction();
  const [editing, setEditing] = React.useState<{ row: AdminPackageRow | null } | null>(null);
  const [deleting, setDeleting] = React.useState<AdminPackageRow | null>(null);
  const { ask, dismiss, question } = useBelowCostQuestion();
  /** The packages ticked for one discount. */
  const [ticked, setTicked] = React.useState<ReadonlySet<string>>(new Set());
  /**
   * What the last successful write did, said on the PAGE. The editor closes on
   * success, so anything said inside it -- "the site is rebuilding", or that
   * it is not -- would close with it.
   */
  const [saved, setSaved] = React.useState<string | null>(null);
  /** Whether the eSIM Go cost check is open. */
  const [checking, setChecking] = React.useState(false);

  const rows = data?.rows ?? [];
  // Read against the rows, so a package gone since it was ticked is not sent.
  const chosen = rows.filter((row) => ticked.has(row.id)).map((row) => row.id);
  const allTicked = rows.length > 0 && chosen.length === rows.length;

  function tick(id: string, on: boolean) {
    setTicked((was) => {
      const next = new Set(was);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function close() {
    setEditing(null);
    setDeleting(null);
    dismiss();
    setSaved(null);
    action.reset();
  }

  function said(answer: WriteOk, verb = "Saved") {
    setSaved(savedNotice(answer, verb));
  }

  /** A write that may be asked about; `done` runs once it has gone through. */
  function askFirst(write: (allowBelowCost: boolean) => Promise<WriteOk>, done: () => void) {
    void ask(action, write, (answer) => {
      reload();
      if (!answer) return;
      said(answer);
      done();
    });
  }

  function save(id: string, body: PackageWrite) {
    const existing = editing?.row;
    askFirst(
      (allow) =>
        existing ? updatePackage(existing.id, body, allow) : createPackage(id, body, allow),
      // Only on success: a refusal -- a duplicate bundle name, most likely --
      // has to leave the form filled in, or the operator retypes it.
      () => setEditing(null),
    );
  }

  /** Starts the storefront's buy panel on this package. */
  function makeDefault(id: string) {
    setSaved(null);
    void action.run(async () => {
      const answer = await putSetting("default_package_id", id);
      said(answer);
      settings.reload();
      return answer;
    });
  }

  /**
   * The supplier's price written as the cost of these packages. No build
   * follows: no page shows a cost. Said from the worker's `changed`, which
   * is what it wrote -- a price can move between the check and the press.
   */
  function syncCosts(ids: string[]): Promise<boolean> {
    setSaved(null);
    return action.run(async () => {
      const answer = await applySupplierCosts(ids);
      const n = answer.changed.length;
      // The rows left alone are said too: "updated" alone would read as all.
      const left = answer.unknown.length > 0 ? `; ${answer.unknown.length} not learned` : "";
      setSaved(`${n} ${n === 1 ? "cost" : "costs"} updated${left}.`);
      reload();
      return answer;
    });
  }

  /** One discount for the ticked packages. */
  function discount(ids: string[], pct: number) {
    setSaved(null);
    askFirst(
      (allow) => setPackageDiscounts(ids, pct, allow),
      () => setTicked(new Set()),
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Products" description="What the shop sells, and for how much.">
        <Button size="sm" variant="outline" onClick={() => setChecking(true)}>
          Check eSIM Go costs
        </Button>
        <Button size="sm" onClick={() => setEditing({ row: null })}>
          Add a package
        </Button>
      </PageHeader>

      {error && <Notice tone="error">{error}</Notice>}
      {settings.error && <Notice tone="error">{settings.error}</Notice>}

      {/* Not a footnote. Between a save and the rebuild it starts, the page
          shows one price and the checkout charges another. */}
      <Notice tone="warning">
        A price saved here is <span className="font-medium">charged at checkout immediately</span>.
        Saving also rebuilds the site, and the plan cards catch up in a few minutes. If a rebuild
        does not start you will be told here, and can{" "}
        <Link to="/publish" className="font-medium underline underline-offset-4">
          publish the site
        </Link>{" "}
        by hand.
      </Notice>

      {saved && editing === null && deleting === null && <Notice tone="success">{saved}</Notice>}
      {editing === null && deleting === null && <Warnings warnings={action.warnings} />}
      {editing === null && deleting === null && action.error && (
        <Notice tone="error">{action.error}</Notice>
      )}

      {checking && (
        <SupplierCosts
          busy={action.busy}
          onApply={syncCosts}
          onClose={() => setChecking(false)}
        />
      )}

      {chosen.length > 0 && (
        <BulkDiscount
          count={chosen.length}
          busy={action.busy}
          onApply={(pct) => discount(chosen, pct)}
          onClear={() => setTicked(new Set())}
        />
      )}

      <DataTable
        columns={12}
        rows={rows}
        loading={loading}
        empty="No packages yet."
        head={
          <>
            <TH>
              <input
                type="checkbox"
                aria-label="Choose every package"
                className="h-4 w-4 rounded border-border"
                checked={allTicked}
                onChange={(e) => setTicked(new Set(e.target.checked ? rows.map((r) => r.id) : []))}
              />
            </TH>
            <TH>Package</TH>
            <TH>Bundle</TH>
            <TH numeric>Data</TH>
            <TH numeric>Days</TH>
            <TH numeric>Cost</TH>
            <TH numeric>Price</TH>
            <TH numeric>Discount</TH>
            <TH numeric>Margin</TH>
            <TH numeric>Sold</TH>
            <TH>State</TH>
            <TH />
          </>
        }
        renderRow={(row) => (
          <PackageRow
            key={row.id}
            row={row}
            busy={action.busy}
            ticked={ticked.has(row.id)}
            onTick={(on) => tick(row.id, on)}
            isDefault={row.id === defaultId}
            onEdit={() => setEditing({ row })}
            onDelete={() => setDeleting(row)}
            onMakeDefault={() => makeDefault(row.id)}
          />
        )}
      />

      <PackageModal
        open={editing !== null}
        row={editing?.row ?? null}
        busy={action.busy}
        error={editing !== null ? action.error : null}
        warnings={action.warnings}
        onClose={close}
        onSave={(id, body) => {
          setSaved(null);
          save(id, body);
        }}
      />

      <BelowCostConfirm {...question} busy={action.busy} />

      <ConfirmModal
        open={deleting !== null}
        title="Delete this package?"
        confirmLabel="Delete it"
        busy={action.busy}
        onClose={close}
        body={
          <>
            {/* In the dialog, for the same reason as the editor's: the race
                where an order lands between the list and this press is the
                one case the worker refuses, and it must be readable. */}
            {action.error && <Notice tone="error">{action.error}</Notice>}
            <p className={action.error ? "mt-3" : undefined}>
              <span className="font-medium">{deleting?.title}</span> will be removed from the catalogue.
            </p>
            <p className="mt-2">Nobody has bought it, so no order points at it. This cannot be undone.</p>
          </>
        }
        onConfirm={async () => {
          if (!deleting) return;
          setSaved(null);
          const ok = await action.run(async () => {
            const answer = await deletePackage(deleting.id);
            said(answer, "Deleted");
            return answer;
          });
          reload();
          if (ok) setDeleting(null);
        }}
      />
    </div>
  );
}

function PackageRow({
  row,
  busy,
  ticked,
  isDefault,
  onTick,
  onEdit,
  onDelete,
  onMakeDefault,
}: {
  row: AdminPackageRow;
  busy: boolean;
  ticked: boolean;
  /** Chosen as the plan the buy panel starts on. */
  isDefault: boolean;
  onTick: (on: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  onMakeDefault: () => void;
}) {
  // On what one is charged at the deepest discount a customer can stack --
  // the worker's figure, so a red margin here is a package it asks about.
  const margin = row.charged_cents - row.cost_cents;
  const sold = row.order_count > 0;
  const onSale = row.enabled === 1;

  return (
    <TR>
      <TD>
        <input
          type="checkbox"
          aria-label={`Choose ${row.title}`}
          className="h-4 w-4 rounded border-border"
          checked={ticked}
          onChange={(e) => onTick(e.target.checked)}
        />
      </TD>
      <TD>
        <div className="font-medium">{row.title}</div>
        <div className="font-mono text-xs text-muted-foreground">{row.id}</div>
      </TD>
      <TD className="max-w-[200px] truncate font-mono text-xs text-secondary">{row.esimgo_name}</TD>
      <TD numeric>{megabytes(row.data_mb)}</TD>
      <TD numeric>{row.validity_days}</TD>
      <TD numeric className="text-secondary">{money(row.cost_cents)}</TD>
      <TD numeric>{money(row.retail_cents)}</TD>
      <TD numeric className={row.discount_pct > 0 ? "font-medium" : "text-muted-foreground"}>
        {row.discount_pct > 0 ? `-${row.discount_pct}%` : "—"}
      </TD>
      {/* A negative margin is a package sold below what it costs to buy, which
          is a typo in one of two fields and worth a colour rather than a
          calculation the operator does in their head. */}
      <TD numeric className={margin < 0 ? "font-medium text-destructive-ink" : "text-secondary"}>
        {money(margin)}
      </TD>
      <TD numeric className="text-muted-foreground">{row.order_count}</TD>
      <TD>
        <div className="flex flex-wrap gap-1">
          {onSale ? <Badge color="green">on sale</Badge> : <Badge color="gray">hidden</Badge>}
          {/* Only on sale: the storefront starts elsewhere when it is not. */}
          {isDefault && onSale && <Badge color="blue">default</Badge>}
        </div>
      </TD>
      <TD>
        <div className="flex items-center justify-end gap-1.5">
          {onSale && !isDefault && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={onMakeDefault}>
              Make default
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={busy} onClick={onEdit}>
            Edit
          </Button>
          {sold ? (
            // Said, not tooltipped. The reason is the useful half: "take it off
            // sale" is what the operator can do instead.
            <span className="text-xs text-muted-foreground">Sold — hide it instead</span>
          ) : (
            <Button size="sm" variant="ghost" disabled={busy} onClick={onDelete}>
              Delete
            </Button>
          )}
        </div>
      </TD>
    </TR>
  );
}
